import { useEffect, useState, useMemo, useRef } from 'react';
import { parseSRT, type SubtitleCue } from '@/utils/srt-parser';
import { parseJSONSubtitles } from '@/utils/json-subtitle-parser';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';


interface LyricsSyncDisplayProps {
  bookId: number;
  pageNumber: number;
  bookTitle?: string;
  content: string; // Full page content passed in
  currentTime: number;
  isPlaying: boolean;
  dominantColor?: string;
  onSeek?: (time: number) => void; // Callback to seek audio to a specific time
}

/**
 * Spotify-style story display for audiobooks
 * Shows page content and highlights words based on SRT timing
 */
export function LyricsSyncDisplay({
  bookId,
  pageNumber,
  bookTitle,
  content,
  currentTime,
  isPlaying,
  dominantColor = '#ffffff',
  onSeek,
}: LyricsSyncDisplayProps) {
  const [cues, setCues] = useState<SubtitleCue[]>([]);
  const [hasTimingData, setHasTimingData] = useState(false);
  const [isLoadingTiming, setIsLoadingTiming] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Split content into paragraphs
  const paragraphs = useMemo(() => {
    if (!content) return [];
    return content
      .split(/\n\n+/)
      .map(p => p.trim())
      .filter(p => p.length > 0);
  }, [content]);

  // Construct subtitle URLs - same folder as MP3, matching name patterns
  const subtitleUrls = useMemo(() => {
    const basePath = `https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/book-media/${bookId}`;
    const paddedNumber = pageNumber.toString().padStart(2, '0');
    const urls: string[] = [];

    // Try book title pattern first (with and without _timing suffix)
    if (bookTitle && !/[:\,\?]/.test(bookTitle)) {
      const encodedTitle = encodeURIComponent(bookTitle);
      urls.push(
        `${basePath}/${paddedNumber}_${encodedTitle}_timing.json`,
        `${basePath}/${paddedNumber}_${encodedTitle}.json`,
        `${basePath}/${paddedNumber}_${encodedTitle}.srt`
      );
    }

    // Chapter pattern with underscores (02_Chapter_1 format) - with and without _timing
    if (pageNumber > 1) {
      const chapterNum = pageNumber - 1;
      // URL-encode the space for "Chapter 1" pattern
      const chapterWithSpace = encodeURIComponent(`${paddedNumber}_Chapter ${chapterNum}_timing.json`);
      const chapterWithSpaceNoTiming = encodeURIComponent(`${paddedNumber}_Chapter ${chapterNum}.json`);
      urls.push(
        // Space pattern (your actual file naming) - URL encoded
        `${basePath}/${chapterWithSpace}`,
        `${basePath}/${chapterWithSpaceNoTiming}`,
        // Underscore pattern
        `${basePath}/${paddedNumber}_Chapter_${chapterNum}_timing.json`,
        `${basePath}/${paddedNumber}_Chapter_${chapterNum}.json`,
        `${basePath}/${paddedNumber}_Chapter_${chapterNum}.srt`
      );
    }

    // Generic patterns
    urls.push(
      `${basePath}/page-${pageNumber}.json`,
      `${basePath}/page-${pageNumber}.srt`
    );

    return urls;
  }, [bookId, pageNumber, bookTitle]);

  // Fetch timing data (SRT/JSON) - optional enhancement
  useEffect(() => {
    let cancelled = false;
    setIsLoadingTiming(true);
    setHasTimingData(false);
    setCues([]);

    async function tryFetchSubtitles() {
      for (const url of subtitleUrls) {
        try {
          const response = await fetch(url, { method: 'GET' });
          if (!response.ok) continue;

          const text = await response.text();
          if (!text.trim()) continue;

          const isJSON = url.endsWith('.json') || text.trim().startsWith('{') || text.trim().startsWith('[');
          const parsedCues = isJSON ? parseJSONSubtitles(text) : parseSRT(text);

          if (parsedCues.length > 0 && !cancelled) {
            setCues(parsedCues);
            setHasTimingData(true);
            setIsLoadingTiming(false);
            return;
          }
        } catch {
          // Continue to next URL
        }
      }
      // No timing data found
      if (!cancelled) {
        setIsLoadingTiming(false);
      }
    }

    tryFetchSubtitles();
    return () => { cancelled = true; };
  }, [subtitleUrls]);

  // Find active cue index based on current time
  // We compute a "target" index from audio time, then animate towards it so we never
  // visually skip short words when the UI updates slower than word durations.
  const activeCueIndex = useMemo(() => {
    if (!cues.length) return -1;

    // Find the last word whose start time has passed
    let activeIdx = -1;
    for (let i = 0; i < cues.length; i++) {
      if (currentTime >= cues[i].startTime) {
        activeIdx = i;
      } else {
        break;
      }
    }
    return activeIdx;
  }, [cues, currentTime]);

  // This is what we actually render as "active". It catches up one-by-one.
  const [displayCueIndex, setDisplayCueIndex] = useState(-1);
  const displayCueIndexRef = useRef(-1);

  // Keep a ref so our catch-up interval doesn't recreate on every tick
  useEffect(() => {
    displayCueIndexRef.current = displayCueIndex;
  }, [displayCueIndex]);

  // Reset scroll and display state when switching pages/books
  useEffect(() => {
    setDisplayCueIndex(-1);
    // Scroll container to top immediately when page changes
    if (containerRef.current) {
      containerRef.current.scrollTo(0, 0);
    }
  }, [bookId, pageNumber]);

  // Track previous activeCueIndex to detect seeks vs natural playback
  const prevActiveCueIndexRef = useRef(-1);

  // Catch-up animator: step through words for natural playback, snap for seeks
  useEffect(() => {
    if (!hasTimingData || !cues.length) return;
    if (activeCueIndex < 0) return;

    const prevActive = prevActiveCueIndexRef.current;
    prevActiveCueIndexRef.current = activeCueIndex;

    // First paint: snap directly to target
    if (displayCueIndexRef.current < 0) {
      setDisplayCueIndex(activeCueIndex);
      return;
    }

    // Seek backwards: snap immediately
    if (activeCueIndex < displayCueIndexRef.current) {
      setDisplayCueIndex(activeCueIndex);
      return;
    }

    // Large jump forward (seek): snap immediately (threshold: 3+ words)
    const jumpSize = activeCueIndex - displayCueIndexRef.current;
    if (jumpSize > 3) {
      setDisplayCueIndex(activeCueIndex);
      return;
    }

    // Small forward movement (natural playback): step through each word
    if (activeCueIndex <= displayCueIndexRef.current) return;

    const MIN_STEP_MS = 70; // minimum time to show each word (prevents "skips")
    const intervalId = window.setInterval(() => {
      setDisplayCueIndex(prev => {
        if (prev >= activeCueIndex) {
          window.clearInterval(intervalId);
          return prev;
        }

        const next = prev + 1;
        if (next >= activeCueIndex) {
          window.clearInterval(intervalId);
        }
        return next;
      });
    }, MIN_STEP_MS);

    return () => window.clearInterval(intervalId);
  }, [activeCueIndex, cues.length, hasTimingData]);

  const cueIndexById = useMemo(() => {
    const map = new Map<number, number>();
    cues.forEach((c, idx) => map.set(c.index, idx));
    return map;
  }, [cues]);

  // Auto-scroll to active paragraph
  useEffect(() => {
    if (displayCueIndex >= 0 && containerRef.current && hasTimingData) {
      const activeEl = containerRef.current.querySelector('[data-active="true"]');
      activeEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [displayCueIndex, hasTimingData]);

  if (!paragraphs.length && !cues.length && !isLoadingTiming) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-white/50 text-base">Loading story...</p>
      </div>
    );
  }

  // If we have timing data, display as sentences with word-level highlighting
  if (hasTimingData && cues.length > 0) {
    // Detect word-level timing (many short cues, typically 1-3 words each)
    const avgWordsPerCue = cues.reduce((sum, c) => sum + c.text.split(' ').length, 0) / cues.length;
    const isWordLevel = cues.length > 15 && avgWordsPerCue <= 3;

    if (isWordLevel) {
      // Group words into sentences based on punctuation (. ! ?)
      const sentences: SubtitleCue[][] = [];
      let currentSentence: SubtitleCue[] = [];
      
      for (const cue of cues) {
        currentSentence.push(cue);
        // Check if this word ends a sentence
        if (/[.!?]$/.test(cue.text.trim())) {
          sentences.push(currentSentence);
          currentSentence = [];
        }
      }
      // Add any remaining words as the last sentence
      if (currentSentence.length > 0) {
        sentences.push(currentSentence);
      }

      return (
        <div ref={containerRef} className="py-4 px-4">
          {sentences.map((sentence, sentenceIdx) => (
            <p
              key={sentenceIdx}
              className="text-left text-xl leading-relaxed font-figtree font-bold tracking-tight mb-4"
            >
              {sentence.map((cue, wordIdx) => {
                const globalCueIndex = cueIndexById.get(cue.index) ?? -1;
                const isActive = globalCueIndex === displayCueIndex;
                const isPast = globalCueIndex >= 0 && globalCueIndex < displayCueIndex;

                return (
                  <span
                    key={cue.index}
                    data-active={isActive}
                    onClick={() => onSeek?.(cue.startTime)}
                    className="transition-all duration-100 cursor-pointer active:scale-95"
                    style={{
                      color: isActive ? '#ffffff' : isPast ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.35)',
                      textShadow: isActive ? '0 2px 8px rgba(0, 0, 0, 0.5)' : 'none',
                    }}
                  >
                    {cue.text}
                    {wordIdx < sentence.length - 1 ? ' ' : ''}
                  </span>
                );
              })}
            </p>
          ))}

          {/* Playing indicator */}
          {isPlaying && (
            <div className="flex items-center justify-start gap-0.5 pt-4">
              <span className="w-0.5 bg-white/30 rounded-full animate-pulse" style={{ height: '6px' }} />
              <span className="w-0.5 bg-white/30 rounded-full animate-pulse" style={{ height: '10px', animationDelay: '0.15s' }} />
              <span className="w-0.5 bg-white/30 rounded-full animate-pulse" style={{ height: '6px', animationDelay: '0.3s' }} />
            </div>
          )}
        </div>
      );
    }

    // Fallback: chunk-based SRT display (continuous text with inline highlighting)
    return (
      <div 
        ref={containerRef}
        className="py-4 px-4"
      >
        <p className="text-left text-xl leading-relaxed font-figtree font-bold tracking-tight">
          {cues.map((cue, idx) => {
            const isActive = idx === displayCueIndex;
            const isPast = idx < displayCueIndex;
            
            return (
              <span
                key={cue.index}
                data-active={isActive}
                onClick={() => onSeek?.(cue.startTime)}
                className="transition-all duration-150 cursor-pointer active:scale-95"
                style={{
                  color: isActive ? '#ffffff' : isPast ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.35)',
                  textShadow: isActive ? '0 2px 8px rgba(0, 0, 0, 0.5)' : 'none',
                }}
              >
                {cue.text}
                {idx < cues.length - 1 ? ' ' : ''}
              </span>
            );
          })}
        </p>

        {/* Playing indicator */}
        {isPlaying && (
          <div className="flex items-center justify-start gap-0.5 pt-4">
            <span className="w-0.5 bg-white/30 rounded-full animate-pulse" style={{ height: '6px' }} />
            <span className="w-0.5 bg-white/30 rounded-full animate-pulse" style={{ height: '10px', animationDelay: '0.15s' }} />
            <span className="w-0.5 bg-white/30 rounded-full animate-pulse" style={{ height: '6px', animationDelay: '0.3s' }} />
          </div>
        )}
      </div>
    );
  }

  // Fallback: show content as sentences without highlighting (same layout as timed version)
  // Split all content into sentences for consistent display
  const allText = paragraphs.join(' ');
  const sentences = allText
    .split(/(?<=[.!?])\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

  return (
    <div 
      ref={containerRef}
      className="py-4 px-4"
    >
      {sentences.map((sentence, idx) => (
        <p
          key={idx}
          className="text-left text-xl leading-relaxed font-figtree font-bold tracking-tight text-white/70 mb-4"
        >
          {sentence}
        </p>
      ))}
    </div>
  );
}
